<template>
    <div class="modern-filter space-y-3 text-left">
        <!-- Search Input (rounded-full style matching blog/mentori) -->
        <div
            class="relative flex items-center bg-white dark:bg-surface-card rounded-full border border-gray-300 dark:border-default"
        >
            <input
                :value="value.q"
                type="text"
                class="block w-full h-[52px] rounded-full bg-transparent py-4 pl-5 pr-24 text-lg placeholder-gray-400 dark:placeholder-txt-body text-gray-900 dark:text-white border-transparent focus:outline-none focus:border-transparent focus:ring-0"
                placeholder="Hľadať vzdelávacie príležitosti..."
                @input="update({ q: ($event.target as HTMLInputElement).value })"
                @keydown.enter="update({ q: ($event.target as HTMLInputElement).value })"
            />
            <div class="absolute inset-y-0 right-0 flex items-center gap-2 pr-1.5">
                <!-- Clear button -->
                <button
                    v-if="value.q"
                    type="button"
                    class="text-gray-400 dark:text-txt-body hover:text-gray-600 dark:hover:text-txt-body"
                    @click="update({ q: '' })"
                >
                    <Icon name="heroicons:x-mark" class="size-5" />
                </button>
                <!-- Search button -->
                <button
                    type="button"
                    class="flex items-center justify-center size-10 rounded-full bg-gray-200 dark:bg-subtle hover:bg-gray-300 dark:hover:bg-subtle transition-colors shrink-0"
                >
                    <Icon name="heroicons:magnifying-glass" class="size-5 text-gray-500 dark:text-txt-body" />
                </button>
            </div>
        </div>

        <!-- Results count + Filter toggle row -->
        <FilterToggleRow
            v-model:show-filters="showFilters"
            :has-active-filters="hasActiveFilters"
            :filter-count="activeFilterCount"
            @reset="resetFilters"
        >
            <slot />
        </FilterToggleRow>

        <!-- Active Filters Chips -->
        <div v-if="hasActiveFilters" class="flex flex-wrap items-center gap-2">
            <!-- Date filter chip -->
            <button
                v-if="value.dateRange && value.dateRange !== 'all'"
                class="group flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1.5 text-xs font-medium text-primary-700 transition-all hover:bg-primary-200 hover:dark:bg-primary-950/40 dark:bg-primary-900/40 dark:text-primary-300"
                @click="clearDateFilter"
            >
                <CIcon name="calendar" class="size-3.5" />
                {{ getDateRangeLabel(value.dateRange) }}
                <CIcon name="close" class="size-3.5 opacity-60 group-hover:opacity-100" />
            </button>

            <!-- Type chips -->
            <button
                v-for="typeId in value.type || []"
                :key="typeId"
                class="group flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-700 transition-all hover:bg-gray-200 hover:dark:bg-subtle dark:bg-subtle dark:text-txt-body"
                @click="toggleSelection('type', typeId)"
            >
                {{ getTypeLabel(typeId) }}
                <CIcon name="close" class="size-3.5 opacity-60 group-hover:opacity-100" />
            </button>

            <!-- Topic chips -->
            <button
                v-for="topic in value.topics || []"
                :key="topic"
                class="group flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-700 transition-all hover:bg-gray-200 hover:dark:bg-subtle dark:bg-subtle dark:text-txt-body"
                @click="removeTopic(topic)"
            >
                #{{ topic }}
                <CIcon name="close" class="size-3.5 opacity-60 group-hover:opacity-100" />
            </button>
        </div>

        <!-- Collapsible Filter Panel -->
        <transition
            enter-active-class="transition-all duration-200 ease-out"
            enter-from-class="opacity-0 -translate-y-2"
            enter-to-class="opacity-100 translate-y-0"
            leave-active-class="transition-all duration-150 ease-in"
            leave-from-class="opacity-100 translate-y-0"
            leave-to-class="opacity-0 -translate-y-2"
        >
            <div
                v-if="showFilters"
                class="rounded-xl border border-gray-200 bg-white p-4 dark:border-default dark:bg-surface-card"
            >
                <div class="grid gap-6 text-left md:grid-cols-2 lg:grid-cols-4">
                    <!-- Keywords Filter -->
                    <div>
                        <p class="mb-2 text-sm text-gray-500 dark:text-txt-body">Vyber kľúčové slová</p>
                        <CFormAutocomplete
                            autoexpand
                            :disabled="isLoadingOptions"
                            no-results-message="Žiadne výsledky pre zadané kľúčové slová"
                            :options="options.topics || []"
                            placeholder="Vyber kľúčové slová"
                            :model-value="value.topics"
                            @update:model-value="update({ topics: $event })"
                        />
                    </div>

                    <!-- Type Filter -->
                    <div v-if="hasTypeVisible">
                        <p class="mb-2 text-sm text-gray-500 dark:text-txt-body">Vyber typ ponuky</p>
                        <div class="space-y-2">
                            <label
                                v-for="option in availableTypes"
                                :key="option.id"
                                class="flex cursor-pointer items-center gap-2.5 rounded-lg p-2 transition-colors hover:bg-gray-50 dark:hover:bg-subtle/50"
                                :class="{ 'bg-primary-50 dark:bg-primary-900/20': isSelected('type', option.id) }"
                            >
                                <input
                                    type="checkbox"
                                    :checked="isSelected('type', option.id)"
                                    class="size-4 rounded border-gray-300 dark:border-default text-primary-500 focus:ring-2 focus:ring-primary-500 focus:ring-offset-0"
                                    @change="toggleSelection('type', option.id)"
                                />
                                <span class="flex-1 text-sm text-gray-700 dark:text-txt-body">
                                    {{ option.label }}
                                </span>
                                <span v-if="option.count" class="text-xs text-gray-400 dark:text-txt-body">
                                    {{ option.count }}
                                </span>
                            </label>
                        </div>
                    </div>

                    <!-- Date Filter -->
                    <div>
                        <p class="mb-2 text-sm text-gray-500 dark:text-txt-body">Vyber termín</p>
                        <div class="space-y-1">
                            <button
                                v-for="preset in datePresets"
                                :key="preset.id"
                                class="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm transition-colors"
                                :class="
                                    value.dateRange === preset.id
                                        ? 'bg-primary-50 font-medium text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
                                        : 'text-gray-600 hover:bg-gray-50 dark:text-txt-body dark:hover:bg-subtle/50'
                                "
                                @click="selectDateRange(preset.id)"
                            >
                                {{ preset.label }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { skillTypeDict } from '#shared/types/dictionary'
import { SkillType } from '#shared/types/Skill'
import { useTypesense } from '@/composables/typesense/useTypesense'
import { useSkills } from '@/composables/skills/useSkills'
import dayjs from 'dayjs'
import 'dayjs/locale/sk'

dayjs.locale('sk')

const { serializeFilters } = useTypesense()
const { loadSkillsFacets } = useSkills()

const datePresets = [
    {
        id: 'next-week',
        label: 'Tento týždeň',
        icon: 'calendar',
        getRange: () => ({ start: dayjs().valueOf(), end: dayjs().add(7, 'days').valueOf() })
    },
    {
        id: 'this-month',
        label: 'Tento mesiac',
        icon: 'calendar',
        getRange: () => ({ start: dayjs().valueOf(), end: dayjs().endOf('month').valueOf() })
    },
    {
        id: 'next-3-months',
        label: 'Najbližšie 3 mesiace',
        icon: 'calendar',
        getRange: () => ({ start: dayjs().valueOf(), end: dayjs().add(3, 'months').valueOf() })
    },
    { id: 'all', label: 'Všetky termíny', icon: 'heroicons:calendar-days', getRange: () => null }
]

export type ForcedFiltersInputTypes = {
    types?: { hidden?: boolean; values?: SkillType[] }
}

const props = defineProps<{
    value: Record<string, any>
    ruleContexts?: string[]
    forcedFilters?: ForcedFiltersInputTypes
}>()

const emit = defineEmits<{
    (e: 'input', value: Record<string, any>): void
}>()

const isLoadingOptions = ref(true)
const options = ref<Record<string, any>>({})
const rawFacets = ref<Record<string, any>>({})
const facetCounts = ref<Record<string, any>>({})
const showFilters = ref(false)

const hasActiveFilters = computed(() => {
    const { q, sortBy, ...filters } = props.value
    return Object.keys(filters).length > 0
})

const activeFilterCount = computed(() => {
    let count = 0
    if (props.value.dateRange && props.value.dateRange !== 'all') count++
    if (props.value.topics?.length) count += props.value.topics.length
    if (props.value.type?.length) count += props.value.type.length
    return count
})

const hasTypeVisible = computed(() => !props.forcedFilters?.types?.hidden)

// Show all types - always clickable, counts shown when available
const availableTypes = computed(() => {
    const allTypes = [SkillType.kurz, SkillType.event, SkillType.workgroup, SkillType.studyOpportunity]
    return allTypes.map((type) => ({
        id: type,
        label: skillTypeDict[type] || type,
        count: facetCounts.value.type?.[type] || 0
    }))
})

onMounted(() => {
    loadOptions()
})

async function loadOptions() {
    isLoadingOptions.value = true

    try {
        const filters = {
            'type': props.forcedFilters?.types?.values
        }

        const facets = await loadSkillsFacets({
            facets: ['topics', 'type'],
            filters: serializeFilters(filters)
        })

        rawFacets.value = facets
        facetCounts.value = facets
        options.value = {
            topics: Object.keys(facets.topics || {})
        }
    } catch (error) {
        console.error('[ModernSkillsFilter] Failed to load facets:', error)
        // Initialize with empty values so UI still renders
        rawFacets.value = {}
        facetCounts.value = {}
        options.value = { topics: [] }
    } finally {
        isLoadingOptions.value = false
    }
}

function getDateRangeLabel(rangeId: string): string {
    return datePresets.find((p) => p.id === rangeId)?.label || rangeId
}

function getTypeLabel(typeId: string): string {
    return skillTypeDict[typeId as SkillType] || typeId
}

function removeTopic(topic: string) {
    const currentTopics = Array.isArray(props.value.topics) ? props.value.topics : []
    update({ topics: currentTopics.filter((t) => t !== topic) })
}

function selectDateRange(presetId: string) {
    const preset = datePresets.find((p) => p.id === presetId)
    if (!preset) return

    const range = preset.getRange()
    if (range === null) {
        const { dateRange, dateStart, dateEnd, ...rest } = props.value
        emit('input', rest)
    } else {
        update({ dateRange: presetId, dateStart: range.start, dateEnd: range.end })
    }
}

function clearDateFilter() {
    const { dateRange, dateStart, dateEnd, ...rest } = props.value
    emit('input', rest)
}

function isSelected(filterKey: string, value: any): boolean {
    const currentValue = props.value[filterKey]
    if (!currentValue) return false
    if (Array.isArray(currentValue)) return currentValue.includes(value)
    return currentValue === value
}

function toggleSelection(filterKey: string, value: any) {
    const currentValue = props.value[filterKey] || []
    const newValue = Array.isArray(currentValue)
        ? currentValue.includes(value)
            ? currentValue.filter((v) => v !== value)
            : [...currentValue, value]
        : [value]
    update({ [filterKey]: newValue.length > 0 ? newValue : undefined })
}

function update(newValue: any) {
    emit('input', { ...props.value, ...newValue })
}

function resetFilters() {
    emit('input', {})
}
</script>
